<!--
출고운임 수동과금 모달 컴포넌트
------------------------------------
사용: app.$refs['charge-shipout-cost'].form(houseId, userId, houseInfo)
디자인 참조: charge-customs-cost.vue
특이: 과금 전용 (환불 없음), 다수 비용항목 + 자동 합계 계산
-->
<template>
    <div class="uk-flex-top" uk-modal esc-close="false" bg-close="false">
        <div class="uk-modal-dialog uk-margin-auto-vertical" style="width: 720px">

            <div class="uk-modal-header">
                <h4 class="uk-text-center" style="margin: 0;">출고운임 수동과금</h4>
                <div class="uk-text-muted uk-text-center" style="margin-top: 4px;">
                    출고운임을 수동으로 계산하여 직접 과금합니다<br>
                    (수동과금된 하우스는 출고후에 과금되지 않습니다.)
                </div>
            </div>

            <div class="uk-modal-body" style="padding: 24px 30px;" uk-overflow-auto>

                <!-- 하우스 정보 카드 -->
                <div v-if="house.code"
                     style="background: #f8fafc; border: 1px solid #edf2f7; border-radius: 10px; padding: 18px 20px; margin-bottom: 16px;">
                    <div style="font-weight: 600; color: #718096; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid #edf2f7;">
                        하우스 정보
                    </div>
                    <div class="uk-grid uk-grid-small uk-child-width-1-2" uk-grid>
                        <div>
                            <app-column header="배송코드" header-size="medium" body-size="medium"
                                header-align="left" body-align="left" horizontal header-width="70px">
                                {{ house.code }}
                            </app-column>
                        </div>
                        <div>
                            <app-column header="수취인" header-size="medium" body-size="medium"
                                header-align="left" body-align="left" horizontal header-width="70px">
                                {{ house.name || '-' }}
                            </app-column>
                        </div>
                        <div>
                            <app-column header="송장번호" header-size="medium" body-size="medium"
                                header-align="left" body-align="left" horizontal header-width="70px">
                                {{ house.blno || '-' }}
                            </app-column>
                        </div>
                        <div>
                            <app-column header="HAWB" header-size="medium" body-size="medium"
                                header-align="left" body-align="left" horizontal header-width="70px">
                                {{ house.hawb || '-' }}
                            </app-column>
                        </div>
                    </div>
                </div>

                <!-- 회원 정보 카드 -->
                <div v-if="member.nick"
                     style="background: #f8fafc; border: 1px solid #edf2f7; border-radius: 10px; padding: 18px 20px; margin-bottom: 24px;">
                    <div style="font-weight: 600; color: #718096; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid #edf2f7;">
                        회원 정보
                    </div>
                    <div class="uk-grid uk-grid-small uk-child-width-1-2" uk-grid>
                        <div>
                            <app-column header="회원사" header-size="medium" body-size="medium"
                                header-align="left" body-align="left" horizontal header-width="70px">
                                {{ member.nick }}
                            </app-column>
                        </div>
                        <div>
                            <app-column header="결제수단" header-size="medium" body-size="medium"
                                header-align="left" body-align="left" horizontal header-width="70px">
                                <span style="display: inline-flex; align-items: center; gap: 6px; background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 2px 10px; font-size: 0.9rem;">
                                    <span style="width: 7px; height: 7px; border-radius: 50%; background: #48bb78; flex-shrink: 0;"></span>
                                    <span v-text="member.pm_label"></span>
                                </span>
                            </app-column>
                        </div>
                    </div>
                </div>

                <!-- 과금 정보 섹션: 무게/구간 -->
                <div style="font-weight: 600; color: #4a5568; margin-bottom: 16px; padding-bottom: 10px; border-bottom: 2px solid #edf2f7;">
                    무게 정보
                </div>

                <div class="uk-margin">
                    <label class="uk-form-label">실무게 (kg)</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.weight">
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">청구무게 (kg)</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.charge_weight">
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">적용구간 (kg)</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.applied_item_weight">
                    </div>
                </div>

                <!-- 과금 정보 섹션: 지역통화 비용 -->
                <div style="font-weight: 600; color: #4a5568; margin-bottom: 16px; padding-bottom: 10px; border-bottom: 2px solid #edf2f7; margin-top: 24px;">
                    지역통화 비용
                </div>

                <div class="uk-margin">
                    <label class="uk-form-label">항공운임 통화</label>
                    <div class="uk-form-controls">
                        <select class="uk-select" v-model="inputs.air_cost_currency">
                            <option v-for="opt in currencyOptions" :key="opt.val" :value="opt.val">{{ opt.text }}</option>
                        </select>
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">항공운임</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.air_cost" @input="calcTotal">
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">포장옵션비용 ({{ regionCurrency }})</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.pack_cost" @input="calcTotal">
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">합배송추가비 ({{ regionCurrency }})</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.over_amount_surcharge" @input="calcTotal">
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">유류할증료 ({{ regionCurrency }})</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.fuel_surcharge" @input="calcTotal">
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">환율</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.region_to_krw_rate" @input="calcTotal"
                               :placeholder="regionCurrency + ' to KRW'">
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">출고지비용 (KRW)</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.region_cost" @input="calcTotal">
                    </div>
                </div>

                <!-- KRW 비용 -->
                <div style="font-weight: 600; color: #4a5568; margin-bottom: 16px; padding-bottom: 10px; border-bottom: 2px solid #edf2f7; margin-top: 24px;">
                    국내 비용 (KRW)
                </div>

                <div class="uk-margin">
                    <label class="uk-form-label">통관/택배</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.domestic_cost" @input="calcTotal">
                    </div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">통관/택배 VAT</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.domestic_cost_vat" @input="calcTotal">
                    </div>
                </div>

                <!-- 최종 출고운임 -->
                <div style="font-weight: 600; color: #4a5568; margin-bottom: 16px; padding-bottom: 10px; border-bottom: 2px solid #edf2f7; margin-top: 24px;">
                    최종 과금액
                </div>

                <div class="uk-margin">
                    <label class="uk-form-label">최종 출고운임 (KRW)</label>
                    <div class="uk-form-controls">
                        <input class="uk-input" type="text" v-model="inputs.shipout_cost"
                               placeholder="이 금액이 과금처리 됩니다"
                               @input="onShipoutCostInput">
                    </div>
                    <div class="uk-text-muted" style="margin-top: 4px;">
                        ※ 카드자동결제 회원사의 경우 수수료 {{ feeRatePercent }}%(VAT별도)가 추가됩니다.
                    </div>
                </div>

                <div class="uk-margin">
                    <label class="uk-form-label">비고</label>
                    <div class="uk-form-controls">
                        <textarea class="uk-textarea" rows="3"
                                  v-model="inputs.memo"
                                  placeholder="수동과금 사유를 입력하세요"></textarea>
                    </div>
                </div>

                <!-- 결제 요약 -->
                <div v-if="shipoutCost > 0"
                     style="border-radius: 10px; padding: 18px 20px; margin-top: 20px; border: 1px solid #e2e8f0; background: #f7fafc;">
                    <div style="font-weight: 600; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0;">
                        결제 요약
                    </div>
                    <div class="uk-flex uk-flex-between" style="padding: 6px 0; color: #718096;">
                        <span>기본과금액</span>
                        <span style="color: #2d3748; font-weight: 500;">{{ numberFormat(shipoutCost) }} 원</span>
                    </div>
                    <div v-if="isCard" class="uk-flex uk-flex-between" style="padding: 6px 0; color: #718096;">
                        <span>카드수수료 ({{ feeRatePercent }}%)</span>
                        <span style="color: #2d3748; font-weight: 500;">{{ numberFormat(feeAmount) }} 원</span>
                    </div>
                    <div v-if="isCard" class="uk-flex uk-flex-between" style="padding: 6px 0; color: #718096;">
                        <span>카드수수료 부가세</span>
                        <span style="color: #2d3748; font-weight: 500;">{{ numberFormat(feeVatAmount) }} 원</span>
                    </div>
                    <div class="uk-flex uk-flex-between"
                         style="border-top: 2px solid #bee3f8; margin-top: 10px; padding-top: 12px; font-weight: 700; font-size: 1.1rem;">
                        <span>최종 결제금액</span>
                        <span style="color: #1e87f0;">{{ numberFormat(totalAmount) }} 원</span>
                    </div>
                </div>

                <!-- 에러 메시지 -->
                <div v-if="errorMsg" class="uk-alert uk-alert-danger" style="margin-top: 16px;">
                    {{ errorMsg }}
                </div>
            </div>

            <div class="uk-modal-footer uk-text-right">
                <div class="uk-text-danger uk-margin-small-bottom">
                    한번 과금되면 취소할 수 없습니다.<br>
                    정말 과금합니까?
                </div>
                <button type="button"
                        class="uk-button uk-button-default uk-modal-close">
                    <span uk-icon="icon: close"></span> 취소
                </button>
                <button type="button"
                        class="uk-button uk-button-primary"
                        :disabled="submitting"
                        @click="submit()">
                    <span uk-icon="icon: check"></span>
                    Charge(과금)
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            csrfToken: String,
            cardFeeRate: { type: Number, default: 0.035 },
            regionCurrency: { type: String, default: '' },
            currencyOptions: { type: Array, default: function () { return []; } },
        },
        data: function () {
            return {
                member: {},
                house: {},
                houseId: null,
                userId: null,
                inputs: {
                    weight: '',
                    charge_weight: '',
                    applied_item_weight: '',
                    air_cost_currency: '',
                    air_cost: '',
                    pack_cost: '',
                    over_amount_surcharge: '',
                    fuel_surcharge: '',
                    region_to_krw_rate: '',
                    region_cost: '',
                    domestic_cost: '',
                    domestic_cost_vat: '',
                    shipout_cost: '',
                    memo: '',
                },
                shipoutCost: 0,
                errorMsg: '',
                submitting: false,
            };
        },
        computed: {
            isCard: function () {
                return this.member.is_card || false;
            },
            feeRatePercent: function () {
                return parseFloat((this.cardFeeRate * 100).toFixed(2));
            },
            feeAmount: function () {
                return this.isCard ? Math.floor(this.shipoutCost * this.cardFeeRate) : 0;
            },
            feeVatAmount: function () {
                return this.isCard ? Math.floor(this.feeAmount * 0.1) : 0;
            },
            totalAmount: function () {
                return this.shipoutCost + this.feeAmount + this.feeVatAmount;
            },
        },
        methods: {
            form: function (house_id, user_id, house_info) {
                this.houseId = house_id;
                this.userId = user_id;
                this.house = house_info || {};
                this.inputs = {
                    weight: '', charge_weight: '', applied_item_weight: '',
                    air_cost_currency: '', air_cost: '',
                    pack_cost: '', over_amount_surcharge: '', fuel_surcharge: '',
                    region_to_krw_rate: '', region_cost: '',
                    domestic_cost: '', domestic_cost_vat: '',
                    shipout_cost: '', memo: '',
                };
                this.shipoutCost = 0;
                this.member = {};
                this.errorMsg = '';
                this.submitting = false;

                // 기본 통화 설정 (selected가 true인 항목, 없으면 첫 항목)
                var defaultCurrency = '';
                for (var i = 0; i < this.currencyOptions.length; i++) {
                    if (this.currencyOptions[i].selected) {
                        defaultCurrency = this.currencyOptions[i].val;
                        break;
                    }
                }
                if (!defaultCurrency && this.currencyOptions.length > 0) {
                    defaultCurrency = this.currencyOptions[0].val;
                }
                this.inputs.air_cost_currency = defaultCurrency;

                var self = this;
                var loadUser = new Promise(function (resolve) {
                    $.app.ajax.module.background({
                        type: 'get',
                        url: '/company-users/' + user_id,
                        contentType: 'application/json',
                        data: {},
                        success: function (response) { resolve(response); },
                    });
                });

                var loadMemberData = new Promise(function (resolve) {
                    $.app.ajax.module.background({
                        type: 'get',
                        url: '/admin/billings/manual-charge/member-data/' + user_id,
                        contentType: 'application/json',
                        data: {},
                        success: function (response) { resolve(response); },
                    });
                });

                Promise.all([loadUser, loadMemberData]).then(function (results) {
                    var userResponse = results[0];
                    var memberData = results[1];

                    self.member = {
                        nick: userResponse.user.nick,
                        pm_label: memberData.pm_label,
                        is_card: memberData.is_card,
                    };

                    UIkit.modal(self.$el).show();
                });
            },

            calcTotal: function () {
                var rate = parseFloat(this.inputs.region_to_krw_rate) || 0;

                // 지역통화 항목 합산 (항공운임, 포장옵션, 합배송추가, 유류할증)
                var regionSum = 0;
                var regionFields = ['air_cost', 'pack_cost', 'over_amount_surcharge', 'fuel_surcharge'];
                for (var i = 0; i < regionFields.length; i++) {
                    regionSum += parseFloat(this.inputs[regionFields[i]]) || 0;
                }

                // KRW 항목 합산
                var regionCost = parseFloat(this.inputs.region_cost) || 0;
                var domesticCost = parseFloat(this.inputs.domestic_cost) || 0;
                var domesticVat = parseFloat(this.inputs.domestic_cost_vat) || 0;

                // 계산 출고운임 = (지역통화합 × 환율) + 출고지비용 + 통관/택배 + VAT
                var total = (rate > 0 ? regionSum * rate : 0) + regionCost + domesticCost + domesticVat;
                total = Math.round(total);

                this.inputs.shipout_cost = total > 0 ? String(total) : '';
                this.shipoutCost = total > 0 ? total : 0;
            },

            onShipoutCostInput: function () {
                this.shipoutCost = parseInt(this.inputs.shipout_cost) || 0;
            },

            submit: function () {
                if (this.shipoutCost < 1) {
                    this.errorMsg = '최종 출고운임은 최소 1원 이상이어야 합니다.';
                    return;
                }

                this.errorMsg = '';
                this.submitting = true;

                var self = this;
                var load = new Promise(function (resolve, reject) {
                    $.app.ajax.module.default({
                        type: 'post',
                        url: '/houses/' + self.houseId + '/shipout-costs',
                        data: {
                            '_token': self.csrfToken,
                            'weight': self.inputs.weight,
                            'charge_weight': self.inputs.charge_weight,
                            'applied_item_weight': self.inputs.applied_item_weight,
                            'air_cost_currency': self.inputs.air_cost_currency,
                            'air_cost': self.inputs.air_cost,
                            'pack_cost': self.inputs.pack_cost,
                            'over_amount_surcharge': self.inputs.over_amount_surcharge,
                            'fuel_surcharge': self.inputs.fuel_surcharge,
                            'region_currency': self.regionCurrency,
                            'region_to_krw_rate': self.inputs.region_to_krw_rate,
                            'region_cost': self.inputs.region_cost,
                            'domestic_cost': self.inputs.domestic_cost,
                            'domestic_cost_vat': self.inputs.domestic_cost_vat,
                            'shipout_cost': self.inputs.shipout_cost,
                            'memo': self.inputs.memo,
                        },
                        success: function (response) { resolve(response); },
                        error: function (jqXHR) { reject(jqXHR); },
                    });
                });

                load.then(function (response) {
                    UIkit.modal(self.$el).hide();
                    UIkit.modal.alert(response.msg).then(function () {
                        $.app.refresh();
                    });
                }).catch(function (jqXHR) {
                    self.submitting = false;

                    if (jqXHR.status === 422 && jqXHR.responseJSON) {
                        var errors = jqXHR.responseJSON;
                        var msgs = [];
                        for (var field in errors) {
                            if (Array.isArray(errors[field])) {
                                msgs.push(errors[field][0]);
                            }
                        }
                        self.errorMsg = msgs.join('\n') || '입력값을 확인하세요.';
                    } else if (jqXHR.responseJSON && jqXHR.responseJSON.msg) {
                        self.errorMsg = jqXHR.responseJSON.msg;
                    } else {
                        self.errorMsg = '요청 처리 중 오류가 발생했습니다.';
                    }
                });
            },

            numberFormat: function (num) {
                return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            },
        },
    };
</script>
